'use client'

import type { ComponentProps, ReactNode } from 'react'
import type { ContactRowState, ReplyChannel, ReplyFeedback } from '@/components/ui/chat'
import type { ChatConversation, ChatReply, ChatSupporter, ChatTagItem, ChatThreadKind } from '@/constants/supporter-response-data'

import { Fragment, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FieldRow } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Sheet, SheetBody, SheetContent, SheetHeader } from '@/components/ui/sheet'
import { WritingStyleDialog } from '@/components/blocks/writing-style-settings'
import { cn } from '@/lib/utils'
import { CHAT_CONVERSATIONS, CHAT_SUPPORTERS, SENT_REPLY_LABEL } from '@/constants/supporter-response-data'

import {
    ArrowLeftIcon,
    ChatDoneIcon,
    ChatOpenIcon,
    FunnelIcon,
    PlusIcon,
    SparkleIcon,
    UserIcon
} from '@/components/icons/icon-set'

import {
    ChatTag,
    ComposerFooter,
    ContactAvatar,
    ContactDetail,
    ContactGroupHeader,
    ContactPhoto,
    ContactRow,
    MessageBubble,
    ReplyComposer,
    SentNotice,
    ShareIndicators,
    ThreadMessage,
    TimelineEvent
} from '@/components/ui/chat'

import {
    Popover,
    PopoverContent,
    PopoverDescription,
    PopoverTitle,
    PopoverTrigger
} from '@/components/ui/popover'

/*
 * GoodUnited / Supporter Response blocks. Figma: 10 / Components / Chat (Inbox panels, Conversation states,
 * Supporter profile, Workspace composition). The page is three panels: ContactList (400px on Colors/surface/subtle),
 * the ConversationPane (fills) and the SupporterProfile (340px), from 1536px up. Between 1280px and 1536px the list is
 * 336px and the profile 280px; below 1280px the profile opens in a drawer, and below 1024px the list and the
 * conversation take turns.
 */

export type ContactTab = 'high-intent' | 'unread' | 'done'

export interface ContactListItem {
    id: string
    initials: string
    src?: string
    summary: string
    preview: string
    time?: string
    group: string
    state: ContactRowState
    /* Whether the conversation is marked as done, for the row's Mark as done or undone button. */
    done?: boolean
}

const SHARE_FILTERS: { value: ChatThreadKind, label: string }[] = [
    { value: 'mission', label: 'Mission statements' },
    { value: 'involvement', label: 'Ways to stay involved' }
]

/* Chat/ContactListToolbar: High-intent, Unread and Done with counts, then the filter. */
interface ContactListToolbarProps {
    tab: ContactTab
    onTabChange: (tab: ContactTab) => void
    counts: Record<ContactTab, number>
    filters?: ChatThreadKind[]
    onFiltersChange?: (filters: ChatThreadKind[]) => void
}

export function ContactListToolbar({ tab, onTabChange, counts, filters = [], onFiltersChange }: ContactListToolbarProps) {
    const [draft, setDraft] = useState<ChatThreadKind[]>(filters)
    const [open, setOpen] = useState(false)

    return (
        <div data-slot='contact-list-toolbar' className='flex shrink-0 items-center gap-2 bg-surface-raised p-3'>
            <Tabs value={tab} onValueChange={value => onTabChange(value as ContactTab)} className='min-w-0 flex-1'>
                <TabsList aria-label='Conversations' className='w-full *:flex-auto'>
                    <TabsTrigger value='high-intent' count={counts['high-intent']} className='px-2 2xl:px-3'>
                        High-intent
                    </TabsTrigger>
                    <TabsTrigger value='unread' count={counts.unread} className='px-2 2xl:px-3'>
                        Unread
                    </TabsTrigger>
                    <TabsTrigger value='done' count={counts.done} className='px-2 2xl:px-3'>
                        Done
                    </TabsTrigger>
                </TabsList>
            </Tabs>
            <Popover
                open={open}
                onOpenChange={next => {
                    setOpen(next)
                    if (next) setDraft(filters)
                }}
            >
                <PopoverTrigger
                    render={<Button variant='outline' size='icon-sm' className='relative' aria-label={filters.length ? `Filters, ${filters.length} applied` : 'Filters'} />}
                >
                    <FunnelIcon aria-hidden='true' />
                    {filters.length > 0 && <span className='absolute -top-1 -right-1 size-2 rounded-full bg-chat-tag-dot' aria-hidden='true' />}
                </PopoverTrigger>
                <PopoverContent align='end'>
                    <div className='flex flex-col gap-1'>
                        <PopoverTitle>
                            Filter conversations
                        </PopoverTitle>
                        <PopoverDescription>
                            Show supporters who shared these.
                        </PopoverDescription>
                    </div>
                    <div className='flex flex-col'>
                        {SHARE_FILTERS.map(filter => (
                            <FieldRow
                                key={filter.value}
                                control={(
                                    <Checkbox
                                        checked={draft.includes(filter.value)}
                                        onCheckedChange={checked => setDraft(current => checked ? [...current, filter.value] : current.filter(item => item !== filter.value))}
                                    />
                                )}
                            >
                                {filter.label}
                            </FieldRow>
                        ))}
                    </div>
                    <div className='flex justify-end gap-2'>
                        <Button variant='ghost' size='sm' onClick={() => setDraft([])}>
                            Clear
                        </Button>
                        <Button
                            size='sm'
                            onClick={() => {
                                onFiltersChange?.(draft)
                                setOpen(false)
                            }}
                        >
                            Apply
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}

/* Chat/ContactList: the title, the toolbar and the Contacts slot, grouped by day with ContactGroupHeader. */
interface ContactListProps extends Omit<ComponentProps<'section'>, 'title' | 'onSelect'> {
    title?: string
    toolbar: ReactNode
    items: ContactListItem[]
    groupLabels?: Record<string, string | undefined>
    onSelect?: (id: string) => void
    /* Adds the Mark as done or undone button to every row, on hover or keyboard focus. */
    onToggleDone?: (id: string) => void
    /* A string shows as one quiet line; any other node is rendered as the empty state itself. */
    emptyMessage?: ReactNode
}

export function ContactList({ title = 'Supporter Response', toolbar, items, groupLabels = {}, onSelect, onToggleDone, emptyMessage = 'No conversations here yet.', className, ...props }: ContactListProps) {
    const groups = items.reduce<{ id: string, items: ContactListItem[] }[]>((all, item) => {
        const group = all.find(entry => entry.id === item.group)
        if (group) group.items.push(item)
        else all.push({ id: item.group, items: [item] })
        return all
    }, [])

    return (
        <section data-slot='contact-list' aria-label={title} className={cn('flex min-h-0 flex-col bg-surface-subtle', className)} {...props}>
            <div className='flex h-16 shrink-0 items-center bg-surface-raised px-5'>
                <h1 className='truncate font-heading text-xl font-semibold text-text-primary'>
                    {title}
                </h1>
            </div>
            {toolbar}
            <div className='relative min-h-0 flex-1 overflow-y-auto'>
                {items.length === 0 && (typeof emptyMessage === 'string'
                    ? (
                        <p className='p-6 text-center text-sm text-text-secondary'>
                            {emptyMessage}
                        </p>
                    )
                    : emptyMessage)}
                {groups.map(group => (
                    <Fragment key={group.id}>
                        {groupLabels[group.id] && <ContactGroupHeader label={groupLabels[group.id] ?? ''} count={group.items.length} />}
                        {group.items.map(item => (
                            <ContactRow
                                key={item.id}
                                initials={item.initials}
                                src={item.src}
                                summary={item.summary}
                                preview={item.preview}
                                time={item.time}
                                state={item.state}
                                done={item.done}
                                onToggleDone={onToggleDone ? () => onToggleDone(item.id) : undefined}
                                onClick={() => onSelect?.(item.id)}
                            />
                        ))}
                    </Fragment>
                ))}
            </div>
        </section>
    )
}

/* Chat/ConversationHeader: who the conversation is with, what they shared and the done action. */
interface ConversationHeaderProps extends Omit<ComponentProps<'header'>, 'children'> {
    initials: string
    src?: string
    name: string
    summary: string
    done: boolean
    onToggleDone: () => void
    onBack?: () => void
    onShowProfile?: () => void
}

export function ConversationHeader({ initials, src, name, summary, done, onToggleDone, onBack, onShowProfile, className, ...props }: ConversationHeaderProps) {
    return (
        <header data-slot='conversation-header' className={cn('flex min-h-20.75 shrink-0 items-center gap-3 border-b border-border-default p-4', className)} {...props}>
            {onBack && (
                <Button variant='ghost' size='icon-sm' className='lg:hidden' onClick={onBack} aria-label='Back to conversations'>
                    <ArrowLeftIcon aria-hidden='true' />
                </Button>
            )}
            <ContactAvatar initials={initials} src={src} />
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <h2 className='truncate text-base font-medium text-text-primary'>
                    {name}
                </h2>
                <p className='truncate text-sm text-text-secondary'>
                    {summary}
                </p>
            </div>
            {onShowProfile && (
                <Button variant='outline' size='icon-sm' className='xl:hidden' onClick={onShowProfile} aria-label='Supporter profile'>
                    <UserIcon aria-hidden='true' />
                </Button>
            )}
            <Button variant='outline' size='sm' onClick={onToggleDone}>
                {done ? <ChatOpenIcon aria-hidden='true' /> : <ChatDoneIcon aria-hidden='true' />}
                {done ? 'Mark as undone' : 'Mark as done'}
            </Button>
        </header>
    )
}

/* Chat/ConversationPane: the header, the Messages slot (scrolls) and the Reply slot pinned at the bottom. */
interface ConversationPaneProps extends Omit<ComponentProps<'section'>, 'children'> {
    header: ReactNode
    messages: ReactNode
    reply?: ReactNode
}

export function ConversationPane({ header, messages, reply, className, ...props }: ConversationPaneProps) {
    return (
        <section data-slot='conversation-pane' aria-label='Conversation' className={cn('flex min-h-0 min-w-0 flex-col bg-surface-raised', className)} {...props}>
            {header}
            <div className='relative flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6'>
                {messages}
            </div>
            {reply && (
                <div className='flex shrink-0 flex-col gap-3 p-4'>
                    {reply}
                </div>
            )}
        </section>
    )
}

/* Chat/SupporterTags: add a tag, the assigned tags, then the AI suggested tags. */
interface SupporterTagsProps {
    tags: ChatTagItem[]
    suggested: ChatTagItem[]
    onAdd: (label: string) => void
    onRemove: (label: string) => void
    onAccept: (label: string) => void
    onDismiss: (label: string) => void
}

export function SupporterTags({ tags, suggested, onAdd, onRemove, onAccept, onDismiss }: SupporterTagsProps) {
    const [value, setValue] = useState('')

    const add = () => {
        const label = value.trim()
        if (!label) return
        onAdd(label)
        setValue('')
    }

    return (
        <div data-slot='supporter-tags' className='flex flex-col gap-4'>
            {/* Figma: the add button sits inside the field, at its right edge. */}
            <form
                data-slot='input-group'
                className='relative w-full'
                onSubmit={event => {
                    event.preventDefault()
                    add()
                }}
            >
                <Input value={value} onChange={event => setValue(event.target.value)} placeholder='Add new tags' aria-label='Add new tags' className='pr-11' />
                <Button
                    type='submit'
                    variant='ghost'
                    size='icon-sm'
                    aria-label='Add tag'
                    disabled={!value.trim()}
                    className='absolute top-1/2 right-1 -translate-y-1/2 text-icon-primary'
                >
                    <PlusIcon aria-hidden='true' />
                </Button>
            </form>
            <p className='text-sm font-medium text-text-secondary'>
                Tags
            </p>
            {tags.length > 0
                ? (
                    <ul className='flex flex-wrap gap-1.5' aria-label='Tags'>
                        {tags.map(tag => (
                            <li key={tag.label} className='max-w-full'>
                                <ChatTag label={tag.label} onRemove={tag.removable ? () => onRemove(tag.label) : undefined} />
                            </li>
                        ))}
                    </ul>
                )
                : (
                    <p className='text-sm text-text-secondary'>
                        No tags yet.
                    </p>
                )}
            {suggested.length > 0 && (
                <>
                    <div className='flex items-center justify-between gap-2'>
                        <p className='text-sm text-text-secondary'>
                            More tags
                        </p>
                        <Badge variant='info' shape='pill'>
                            <SparkleIcon aria-hidden='true' className='text-icon-primary' />
                            AI suggested
                        </Badge>
                    </div>
                    <ul className='flex flex-wrap gap-1.5' aria-label='Suggested tags'>
                        {suggested.map(tag => (
                            <li key={tag.label} className='max-w-full'>
                                <ChatTag
                                    kind='suggested'
                                    label={tag.label}
                                    onSelect={() => onAccept(tag.label)}
                                    onRemove={tag.removable ? () => onDismiss(tag.label) : undefined}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    )
}

/*
 * Chat/SupporterProfile: the title, the supporter, the Profile details slot and the Tags slot (340px panel).
 * The supporter leads with ContactPhoto, the tilted print, beside the name (Poppins / text-xl) and the location.
 * The Messenger and email lines sit under the photo in the 280px and 340px panels, and beside it from 320px of
 * content up (the profile drawer).
 */
interface SupporterProfileProps extends Omit<ComponentProps<'section'>, 'title'> {
    supporter: Pick<ChatSupporter, 'initials' | 'fullName' | 'location' | 'messengerName' | 'email' | 'photo'>
    title?: string | null
    tags?: ReactNode
}

export function SupporterProfile({ supporter, title = 'Supporter profile', tags, className, ...props }: SupporterProfileProps) {
    return (
        <section data-slot='supporter-profile' aria-label='Supporter profile' className={cn('flex flex-col gap-6 bg-surface-raised p-6', className)} {...props}>
            {title && (
                <h2 className='font-heading text-xl font-semibold text-text-primary'>
                    {title}
                </h2>
            )}
            <div className='@container'>
                <div className='grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-5 gap-y-5 @xs:items-start @xs:gap-y-3'>
                    <ContactPhoto initials={supporter.initials} src={supporter.photo} className='@xs:row-span-2' />
                    <div className='flex min-w-0 flex-col gap-1 @xs:pt-2'>
                        <p className='font-heading text-xl font-semibold break-words text-text-primary'>
                            {supporter.fullName}
                        </p>
                        <p className='text-base text-text-secondary'>
                            <span className='sr-only'>Location: </span>
                            {supporter.location}
                        </p>
                    </div>
                    <div className='col-span-2 flex flex-col gap-2 @xs:col-span-1'>
                        <ContactDetail kind='messenger'>{supporter.messengerName}</ContactDetail>
                        <ContactDetail kind='email'>{supporter.email}</ContactDetail>
                    </div>
                </div>
            </div>
            {tags}
        </section>
    )
}

/*
 * Chat/Workspace: the whole Supporter Response inbox, interactive. Pick a conversation, edit the drafted
 * reply, switch between Messenger and email, send or copy it, mark it done or undone, write another,
 * rate the draft, manage tags and open the writing-style settings.
 */
interface ConversationState {
    done: boolean
    unread: boolean
    replies: ChatReply[]
    composing: boolean
    channel: ReplyChannel
    message: string
    subject: string
    feedback: ReplyFeedback
}

export type SupporterResponseState = 'draft' | 'sent' | 'email-marked-sent'

const initialConversation = (conversation: ChatConversation): ConversationState => ({
    done: conversation.done,
    unread: conversation.unread,
    replies: conversation.replies,
    composing: conversation.replies.length === 0,
    channel: 'messenger',
    message: conversation.draft,
    subject: conversation.subject,
    feedback: null
})

const seedConversations = (state: SupporterResponseState) => Object.fromEntries(CHAT_CONVERSATIONS.map(conversation => {
    const seeded = initialConversation(conversation)
    if (conversation.id === 'brandon-today' && state === 'sent') {
        return [conversation.id, { ...seeded, done: true, composing: false, replies: [{ channel: 'messenger', label: SENT_REPLY_LABEL, message: conversation.draft }] }]
    }
    if (conversation.id === 'brandon-today' && state === 'email-marked-sent') {
        return [conversation.id, { ...seeded, done: true, channel: 'email', replies: [{ channel: 'email', label: 'You copied the response and replied via email' }] }]
    }
    return [conversation.id, seeded]
})) as Record<string, ConversationState>

const nowLabel = () => {
    const now = new Date()
    const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    return `You replied · ${time}, ${date}`
}

const GROUP_LABELS: Record<string, string | undefined> = { today: undefined, yesterday: 'Yesterday' }

const sharedSummary = (conversation: ChatConversation) => {
    const kinds = new Set(conversation.thread.map(item => item.kind))
    return [kinds.has('mission') && 'Mission statement', kinds.has('involvement') && 'Stay involved'].filter(Boolean).join(' · ')
}

interface SupporterResponseWorkspaceProps extends Omit<ComponentProps<'div'>, 'children'> {
    state?: SupporterResponseState
    writingStyleOpen?: boolean
}

export function SupporterResponseWorkspace({ state = 'draft', writingStyleOpen = false, className, ...props }: SupporterResponseWorkspaceProps) {
    const { toast } = useToast()
    const [conversations, setConversations] = useState(() => seedConversations(state))
    const [supporters, setSupporters] = useState<ChatSupporter[]>(CHAT_SUPPORTERS)
    const [selectedId, setSelectedId] = useState('brandon-today')
    const [tab, setTab] = useState<ContactTab>('high-intent')
    const [filters, setFilters] = useState<ChatThreadKind[]>([])
    const [mobileView, setMobileView] = useState<'list' | 'conversation'>('list')
    const [profileOpen, setProfileOpen] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(writingStyleOpen)

    const supporterOf = (conversation: ChatConversation) => supporters.find(item => item.id === conversation.supporterId) ?? supporters[0]

    const counts = useMemo(() => ({
        'high-intent': CHAT_CONVERSATIONS.length,
        'unread': CHAT_CONVERSATIONS.filter(item => conversations[item.id].unread).length,
        'done': CHAT_CONVERSATIONS.filter(item => conversations[item.id].done).length
    }), [conversations])

    const items: ContactListItem[] = CHAT_CONVERSATIONS
        .filter(item => tab === 'high-intent' || (tab === 'unread' ? conversations[item.id].unread : conversations[item.id].done))
        .filter(item => filters.length === 0 || item.thread.some(entry => filters.includes(entry.kind)))
        .map(item => {
            const current = conversations[item.id]
            const supporter = supporterOf(item)
            return {
                id: item.id,
                initials: supporter.initials,
                src: supporter.photo,
                summary: `${supporter.name} · ${item.topic}`,
                preview: item.preview,
                time: item.time,
                group: item.group,
                state: item.id === selectedId ? 'selected' : current.done ? 'done' : current.unread ? 'unread' : 'default',
                done: current.done
            }
        })

    const selected = CHAT_CONVERSATIONS.find(item => item.id === selectedId) ?? CHAT_CONVERSATIONS[0]
    const current = conversations[selected.id]
    const supporter = supporterOf(selected)

    const update = (changes: Partial<ConversationState>) => setConversations(all => ({ ...all, [selected.id]: { ...all[selected.id], ...changes } }))

    const updateSupporter = (change: (value: ChatSupporter) => ChatSupporter) => setSupporters(all => all.map(item => item.id === supporter.id ? change(item) : item))

    const select = (id: string) => {
        setSelectedId(id)
        setConversations(all => ({ ...all, [id]: { ...all[id], unread: false } }))
        setMobileView('conversation')
    }

    const send = () => {
        update({ replies: [...current.replies, { channel: 'messenger', label: nowLabel(), message: current.message.trim() }], done: true, composing: false })
        toast({ title: `Reply sent to ${supporter.name}`, description: 'The conversation is marked as done.' })
    }

    const copyEmail = async () => {
        const text = `Subject: ${current.subject}\n\n${current.message.trim()}`
        try {
            await navigator.clipboard.writeText(text)
        }
        catch {
            // Clipboard access can be refused; the email still opens with the reply filled in.
        }
        const link = document.createElement('a')
        link.href = `mailto:${supporter.email}?subject=${encodeURIComponent(current.subject)}&body=${encodeURIComponent(current.message.trim())}`
        link.click()
        update({ replies: [...current.replies, { channel: 'email', label: 'You copied the response and replied via email' }], done: true })
        toast({ title: 'Response copied', description: `Your email app opens a message to ${supporter.email}. You can also paste the reply.`, tone: 'info' })
    }

    const toggleDone = (id = selected.id) => {
        const conversation = CHAT_CONVERSATIONS.find(item => item.id === id) ?? selected
        const wasDone = conversations[id].done
        setConversations(all => ({ ...all, [id]: { ...all[id], done: !wasDone } }))
        toast({ title: wasDone ? 'Marked as undone' : 'Marked as done', description: `${supporterOf(conversation).name} · ${conversation.topic}`, tone: 'info' })
    }

    const writeAnother = () => update({ composing: true, message: selected.draft, subject: selected.subject, feedback: null })

    const rate = (feedback: ReplyFeedback) => {
        update({ feedback })
        if (feedback) toast({ title: 'Thanks for the feedback', description: 'GoodUnited AI uses it to write better drafts.', tone: 'info' })
    }

    const tagsPanel = (
        <SupporterTags
            tags={supporter.tags}
            suggested={supporter.suggestedTags}
            onAdd={label => updateSupporter(value => value.tags.some(tag => tag.label.toLowerCase() === label.toLowerCase())
                ? value
                : { ...value, tags: [...value.tags, { label, removable: true }] })}
            onRemove={label => updateSupporter(value => ({ ...value, tags: value.tags.filter(tag => tag.label !== label) }))}
            onAccept={label => updateSupporter(value => ({
                ...value,
                tags: value.tags.some(tag => tag.label === label) ? value.tags : [...value.tags, { label, removable: true }],
                suggestedTags: value.suggestedTags.filter(tag => tag.label !== label)
            }))}
            onDismiss={label => updateSupporter(value => ({ ...value, suggestedTags: value.suggestedTags.filter(tag => tag.label !== label) }))}
        />
    )

    const messages = (
        <>
            {selected.thread.map(entry => (
                <ThreadMessage
                    key={entry.message}
                    name={supporter.name}
                    initials={supporter.initials}
                    src={supporter.photo}
                    context={entry.kind === 'mission' ? 'shared their mission story in' : 'shared how they want to stay involved in'}
                    journey={entry.journey}
                >
                    <MessageBubble>
                        {entry.message}
                    </MessageBubble>
                </ThreadMessage>
            ))}
            {current.replies.length === 0 && !current.done && (
                <TimelineEvent
                    kind='waiting'
                    name={supporter.name}
                    trailing={(
                        <ShareIndicators
                            mission={selected.thread.some(entry => entry.kind === 'mission')}
                            involvement={selected.thread.some(entry => entry.kind === 'involvement')}
                        />
                    )}
                >
                    is waiting for a response
                </TimelineEvent>
            )}
            {current.replies.map((reply, index) => (
                <Fragment key={`${reply.label}-${index}`}>
                    <TimelineEvent kind={reply.channel === 'messenger' ? 'replied' : 'email-replied'}>
                        {reply.label}
                    </TimelineEvent>
                    {reply.message && (
                        <MessageBubble direction='outgoing'>
                            {reply.message}
                        </MessageBubble>
                    )}
                </Fragment>
            ))}
            {current.done && (
                <TimelineEvent kind='done'>
                    Marked as done
                </TimelineEvent>
            )}
        </>
    )

    const reply = current.composing
        ? (
            <>
                <TimelineEvent kind='ai-draft'>
                    {`GoodUnited AI drafted a response for ${supporter.name}`}
                </TimelineEvent>
                <ReplyComposer
                    channel={current.channel}
                    onChannelChange={channel => update({ channel })}
                    message={current.message}
                    onMessageChange={message => update({ message })}
                    subject={current.subject}
                    onSubjectChange={subject => update({ subject })}
                    onSend={send}
                    onCopyEmail={copyEmail}
                    label={`Reply to ${supporter.name}`}
                />
                <ComposerFooter onWritingStyle={() => setSettingsOpen(true)} feedback={current.feedback} onFeedback={rate} />
            </>
        )
        : <SentNotice onWriteAnother={writeAnother} />

    return (
        <div data-slot='supporter-response-workspace' className={cn('flex h-full min-h-0 bg-surface-raised', className)} {...props}>
            <ContactList
                className={cn('w-full border-border-default lg:flex lg:w-84 lg:shrink-0 lg:border-r 2xl:w-100', mobileView === 'list' ? 'flex' : 'hidden')}
                toolbar={<ContactListToolbar tab={tab} onTabChange={setTab} counts={counts} filters={filters} onFiltersChange={setFilters} />}
                items={items}
                groupLabels={GROUP_LABELS}
                onSelect={select}
                onToggleDone={toggleDone}
                emptyMessage={tab === 'unread' ? 'You are all caught up.' : 'No conversations match these filters.'}
            />
            <ConversationPane
                className={cn('flex-1 lg:flex', mobileView === 'conversation' ? 'flex' : 'hidden')}
                header={(
                    <ConversationHeader
                        initials={supporter.initials}
                        src={supporter.photo}
                        name={supporter.name}
                        summary={sharedSummary(selected)}
                        done={current.done}
                        onToggleDone={() => toggleDone()}
                        onBack={() => setMobileView('list')}
                        onShowProfile={() => setProfileOpen(true)}
                    />
                )}
                messages={messages}
                reply={reply}
            />
            <SupporterProfile
                supporter={supporter}
                tags={tagsPanel}
                className='relative hidden w-70 shrink-0 overflow-y-auto border-l border-border-default xl:flex 2xl:w-85'
            />

            <Sheet open={profileOpen} onOpenChange={setProfileOpen}>
                <SheetContent size='sm' className='xl:hidden'>
                    <SheetHeader title='Supporter profile' />
                    <SheetBody className='p-0'>
                        <SupporterProfile supporter={supporter} title={null} tags={tagsPanel} />
                    </SheetBody>
                </SheetContent>
            </Sheet>

            <WritingStyleDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </div>
    )
}
